import TemplateFormFieldModel from "../models/templating/api/TemplateFormFieldModel";
import TemplateFormFieldValueModel from "../models/templating/api/TemplateFormFieldValueModel";

export type TemplateFieldLike = Partial<TemplateFormFieldModel | TemplateFormFieldValueModel>;

function getTemplateFormFieldId(field: TemplateFieldLike): string | undefined {
    const value = field as Partial<TemplateFormFieldValueModel>;
    return ('id' in field ? field.id : undefined) || value.templateFormFieldId;
}

export function getFieldIdentity(field: TemplateFieldLike): string | undefined {
    return field.associatedVariable || field.title || getTemplateFormFieldId(field);
}

export function fieldsMatch(first: TemplateFieldLike, second: TemplateFieldLike): boolean {
    if (!first || !second) {
        return false;
    }

    if (first.associatedVariable && second.associatedVariable) {
        return first.associatedVariable === second.associatedVariable;
    }

    const firstId = getTemplateFormFieldId(first);
    const secondId = getTemplateFormFieldId(second);
    if (firstId && secondId && firstId === secondId) {
        return true;
    }

    if (first.title && second.title) {
        return first.title === second.title;
    }

    return false;
}

function hasMeaningfulValue(fieldValue: TemplateFormFieldValueModel): boolean {
    const value = fieldValue.value?.trim();
    const defaultValue = fieldValue.defaultValue?.trim();

    if (!value || value === 'null') {
        return false;
    }

    return value !== defaultValue && value !== fieldValue.associatedVariable;
}

function getBestFieldValue(fieldValues: TemplateFormFieldValueModel[]): TemplateFormFieldValueModel | undefined {
    return fieldValues.find(hasMeaningfulValue) || fieldValues[0];
}

export function findFieldValueForField(formField: TemplateFormFieldModel, fieldValues: TemplateFormFieldValueModel[] = []): TemplateFormFieldValueModel | undefined {
    if (formField.associatedVariable) {
        const associatedVariableMatches = fieldValues.filter(fv => fv.associatedVariable === formField.associatedVariable);
        const exactIdMatches = associatedVariableMatches.filter(fv => fv.templateFormFieldId === formField.id);
        return getBestFieldValue(exactIdMatches)
            || getBestFieldValue(associatedVariableMatches)
            || fieldValues.find(fv => fv.templateFormFieldId === formField.id && !fv.associatedVariable)
            || getBestFieldValue(fieldValues.filter(fv => formField.title && fv.title === formField.title && !fv.associatedVariable));
    }

    return getBestFieldValue(fieldValues.filter(fv => formField.id && fv.templateFormFieldId === formField.id))
        || getBestFieldValue(fieldValues.filter(fv => formField.title && fv.title === formField.title));
}

export function dedupeFieldValuesForField(formField: TemplateFormFieldModel, fieldValues: TemplateFormFieldValueModel[] = []): TemplateFormFieldValueModel[] {
    const matchingValue = findFieldValueForField(formField, fieldValues);

    return fieldValues.filter(fv => {
        if (matchingValue && fv === matchingValue) {
            return true;
        }

        return !fieldsMatch(formField, fv);
    });
}

export function normalizeFieldValueForField(formField: TemplateFormFieldModel, fieldValue: TemplateFormFieldValueModel): TemplateFormFieldValueModel {
    return {
        ...fieldValue,
        fieldType: formField.fieldType,
        tip: formField.tip,
        title: formField.title,
        label: formField.label,
        defaultValue: formField.defaultValue,
        associatedVariable: formField.associatedVariable,
        placeholder: formField.placeholder,
        templateFormFieldId: formField.id,
    };
}
